import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../firebase/config';

class AuthService {
  constructor() {
    this.user = null;
    this.isAuthenticated = false;
    this.authInitialized = false;
    
    // Set up auth state listener
    onAuthStateChanged(auth, (user) => {
      this.user = user;
      this.isAuthenticated = !!user;
      this.authInitialized = true;
    });
  }

  // Sign in with email and password
  async signInWithEmail(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { user: userCredential.user, error: null };
    } catch (error) {
      return { user: null, error: this.getErrorMessage(error.code) };
    }
  }  // Sign up with email and password
  async signUpWithEmail(email, password, displayName = '') {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name if provided
      if (displayName) {
        await updateProfile(userCredential.user, {
          displayName: displayName
        });
      }
      
      // Send email verification
      await sendEmailVerification(userCredential.user);
      
      // Create user in MongoDB with default permissions (all false)
      await this.createUserInDatabase(userCredential.user);
      
      return { 
        user: userCredential.user, 
        error: null,
        message: 'Account created successfully! Please check your email for verification before you can access the platform.'
      };
    } catch (error) {
      return { user: null, error: this.getErrorMessage(error.code) };
    }
  }
  // Sign in with Google
  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      
      // Check if user exists in database, if not create them
      const existingUser = await this.getUserFromDatabase(userCredential.user.uid);
      if (!existingUser) {
        await this.createUserInDatabase(userCredential.user);
      }
      
      return { user: userCredential.user, error: null };
    } catch (error) {
      return { user: null, error: this.getErrorMessage(error.code) };
    }
  }

  // Sign out
  async signOut() {
    try {
      await signOut(auth);
      return { error: null };
    } catch (error) {
      return { error: this.getErrorMessage(error.code) };
    }
  }

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  }
  // Check if user is authenticated
  isUserAuthenticated() {
    return this.isAuthenticated;
  }

  // Check if auth is initialized
  isAuthInitialized() {
    return this.authInitialized;
  }

  // Wait for auth to be initialized
  waitForAuthInitialization() {
    return new Promise((resolve) => {
      if (this.authInitialized) {
        resolve(this.isAuthenticated);
      } else {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(!!user);
        });
      }
    });
  }

  // Auth state listener
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  }
  // Create user in MongoDB database
  async createUserInDatabase(firebaseUser) {
    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL || 'http://localhost:5000'}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || '',
          emailVerified: firebaseUser.emailVerified,
          createdAt: new Date().toISOString(),          
          permissions: {
            home: false,
            vehicles: false,
            locations: false,
            analytics: false,
            history: false,
            ai_tools: false
          },
          status: 'pending'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create user in database');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating user in database:', error);
      throw error;
    }
  }

  // Get user from database
  async getUserFromDatabase(uid) {
    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL || 'http://localhost:5000'}/api/users/${uid}`);
      
      if (response.status === 404) {
        return null; // User doesn't exist
      }
      
      if (!response.ok) {
        throw new Error('Failed to get user from database');
      }
      
      const data = await response.json();
      return data.user;
    } catch (error) {
      console.error('Error getting user from database:', error);
      return null;
    }
  }

  // Update user email verification status
  async updateEmailVerificationStatus(uid, emailVerified) {
    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL || 'http://localhost:5000'}/api/users/${uid}/email-verified`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailVerified: emailVerified
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update email verification status');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating email verification status:', error);
      throw error;
    }
  }

  // Get user permissions from database
  async getUserPermissions(uid) {
    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL || 'http://localhost:5000'}/api/users/${uid}/permissions`);
      
      if (!response.ok) {
        throw new Error('Failed to get user permissions');
      }
      
      const data = await response.json();
      return data.permissions;
    } catch (error) {
      console.error('Error getting user permissions:', error);      return {
        home: false,
        vehicles: false,
        locations: false,
        analytics: false,
        history: false,
        ai_tools: false
      };
    }
  }
  // Check if user has any permissions
  async hasAnyPermissions(uid) {
    const permissions = await this.getUserPermissions(uid);
    return Object.values(permissions).some(permission => permission === true);
  }

  // Get available pages for user based on permissions
  async getAvailablePages(uid) {
    try {
      const permissions = await this.getUserPermissions(uid);
      return Object.keys(permissions).filter(page => permissions[page] === true);
    } catch (error) {
      console.error('Error getting available pages:', error);
      return [];
    }
  }

  // Get user status from database
  async getUserStatus(uid) {
    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL || 'http://localhost:5000'}/api/users/${uid}/permissions`);
      
      if (!response.ok) {
        throw new Error('Failed to get user status');
      }
      
      const data = await response.json();
      return {
        status: data.status || 'pending',
        emailVerified: data.emailVerified || false,
        permissions: data.permissions || {
          home: false,
          vehicles: false,
          locations: false,
          analytics: false,
          history: false,
          ai_tools: false
        }
      };
    } catch (error) {
      console.error('Error getting user status:', error);
      return {
        status: 'pending',
        emailVerified: false,        permissions: {
          home: false,
          vehicles: false,
          locations: false,
          analytics: false,
          history: false,
          ai_tools: false
        }
      };
    }
  }

  // Check if user can access application (email verified and has permissions)
  async canAccessApplication(uid) {
    try {
      const userStatus = await this.getUserStatus(uid);
      return userStatus.emailVerified && Object.values(userStatus.permissions).some(p => p === true);
    } catch (error) {
      console.error('Error checking application access:', error);
      return false;
    }
  }

  // Check if user email is verified
  isEmailVerified() {
    return this.user?.emailVerified || false;
  }

  // Resend email verification
  async resendEmailVerification() {
    try {
      if (this.user && !this.user.emailVerified) {
        await sendEmailVerification(this.user);
        return { error: null };
      }
      return { error: 'Email already verified or user not found' };
    } catch (error) {
      return { error: this.getErrorMessage(error.code) };
    }
  }

  // Helper method to convert Firebase error codes to user-friendly messages
  getErrorMessage(errorCode) {
    const errorMessages = {
      'auth/user-not-found': 'No account found with this email address.',
      'auth/wrong-password': 'Incorrect password. Please try again.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password is too weak. Please choose a stronger password.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/popup-closed-by-user': 'Sign-in popup was closed before completion.',
      'auth/cancelled-popup-request': 'Only one popup request is allowed at a time.',
      'auth/popup-blocked': 'Sign-in popup was blocked by the browser.',
      'auth/invalid-credential': 'Invalid credentials provided.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
      'default': 'An unexpected error occurred. Please try again.'
    };

    return errorMessages[errorCode] || errorMessages.default;
  }
}

export default new AuthService();
